import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '@auth/services/http/auth/auth';
import { extractApiErrorCode } from '@core/api/api-extract-error';
import { SessionStore } from '@core/store/session.store';
import { environment } from '@env';

const accessTokenExpiredCode = 'ACCESS_TOKEN_EXPIRED';
const invalidRefreshTokenCode = 'INVALID_REFRESH_TOKEN';
const apiBase = environment.apiUrl.replace(/\/+$/, '');
const refreshUrl = `${apiBase}/auth/refresh`;

function isApiRequest(req: HttpRequest<unknown>): boolean {
  return req.url === apiBase || req.url.startsWith(`${apiBase}/`);
}

function isRefreshRequest(req: HttpRequest<unknown>): boolean {
  return req.url === refreshUrl;
}

function withAccessToken(
  req: HttpRequest<unknown>,
  accessToken: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

function shouldLogoutAfterRefreshFailure(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse &&
    err.status === HttpStatusCode.Unauthorized &&
    extractApiErrorCode(err.error) === invalidRefreshTokenCode
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionStore);
  const auth = inject(AuthService);
  const token = session.getAccessToken();

  if (!token || !isApiRequest(req) || isRefreshRequest(req)) {
    return next(req);
  }

  const authReq = withAccessToken(req, token);

  return next(authReq).pipe(
    catchError((err: unknown) => {
      if (
        !(err instanceof HttpErrorResponse) ||
        err.status !== HttpStatusCode.Unauthorized ||
        extractApiErrorCode(err.error) !== accessTokenExpiredCode
      ) {
        return throwError(() => err);
      }

      return auth.refreshAccessToken().pipe(
        catchError((refreshErr: unknown) => {
          if (shouldLogoutAfterRefreshFailure(refreshErr)) {
            auth.logout();
          }

          return throwError(() => refreshErr);
        }),
        switchMap((accessToken) => next(withAccessToken(req, accessToken))),
      );
    }),
  );
};
